import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { AlertTriangle, RefreshCw, LogIn } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void, isAuthError: boolean) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isAuthError: boolean;
}

class AuthAwareErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isAuthError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Check if it's an auth-related error
    const isAuthError = 
      error.message?.includes('JWT') ||
      error.message?.includes('token') ||
      error.message?.includes('401') ||
      error.message?.includes('403') ||
      error.message?.includes('authenticated') ||
      error.message?.includes('session');

    return {
      hasError: true,
      error,
      isAuthError
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only report non-auth errors to Sentry
    if (!this.state.isAuthError) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
    
    console.error('Error caught by boundary:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      isAuthError: false
    });
  };

  private handleSignIn = () => {
    // Redirect to sign in with current path as redirect
    const currentPath = window.location.pathname;
    window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`;
  };

  private handleRefreshAndRetry = async () => {
    try {
      // Try to refresh the session
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      if (data.session) {
        // Session refreshed successfully, reset the error boundary
        this.handleReset();
      } else {
        // No session available, redirect to sign in
        this.handleSignIn();
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      this.handleSignIn();
    }
  };

  public render() {
    if (this.state.hasError) {
      const { error, isAuthError } = this.state;

      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(error!, this.handleReset, isAuthError);
      }

      // Auth error UI
      if (isAuthError) {
        return (
          <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
            <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-yellow-400/10 rounded-full p-3">
                  <AlertTriangle className="w-8 h-8 text-yellow-400" />
                </div>
              </div>
              
              <h1 className="text-xl font-bold text-white mb-2">Session Expired</h1>
              <p className="text-dash-text-muted mb-6">
                Your session has expired or is invalid. Please sign in again to continue.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={this.handleRefreshAndRetry}
                  className="w-full bg-yellow-400 text-black px-4 py-3 rounded-md font-medium hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try to Refresh Session
                </button>
                
                <button
                  onClick={this.handleSignIn}
                  className="w-full bg-zinc-800 text-white px-4 py-3 rounded-md font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In Again
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Generic error UI
      return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-500/10 rounded-full p-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </div>
            
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-dash-text-muted mb-6">
              We've been notified and will fix this issue as soon as possible.
            </p>
            
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-dash-text-secondary cursor-pointer hover:text-dash-text-primary">
                  Error details
                </summary>
                <pre className="mt-2 text-xs text-dash-text-secondary bg-dash-bg-elevated p-3 rounded-dash-md overflow-auto">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-yellow-400 text-black px-4 py-3 rounded-md font-medium hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthAwareErrorBoundary;